import axios from 'axios'
import { config } from '../config'
import apiClient from '../utils/apiClient'

export class AdminClient {
	#adminToken = null
	#impersonationToken = null

	constructor(http = axios.create({ baseURL: config.apiUrl }), elevationHttp = apiClient) {
		this.http = http
		this.elevationHttp = elevationHttp
	}

	hasAdminSession() { return Boolean(this.#adminToken) }
	hasImpersonationSession() { return Boolean(this.#impersonationToken) }

	async elevate(password) {
		const response = await this.elevationHttp.post('/api/admin/session/elevate', { password })
		const { token, ...session } = response.data
		this.#adminToken = token
		return session
	}

	async adminRequest(method, url, data, params) {
		if (!this.#adminToken) throw Object.assign(new Error('Admin session required'), { code: 'ADMIN_AUTH_REQUIRED' })
		try {
			return (await this.http.request({
				method, url, data, params,
				headers: { Authorization: `Admin ${this.#adminToken}` }, // never inherit a tenant header
			})).data
		} catch (error) {
			if (error.response?.status === 401) this.#adminToken = null
			throw error
		}
	}

	get(url, params) { return this.adminRequest('get', url, undefined, params) }
	post(url, data) { return this.adminRequest('post', url, data) }
	put(url, data) { return this.adminRequest('put', url, data) }
	delete(url) { return this.adminRequest('delete', url) }

	async logout() {
		try { if (this.#adminToken) await this.delete('/api/admin/session') }
		finally { this.#adminToken = null }
	}

	setImpersonationToken(token) {
		if (!token || typeof token !== 'string') throw new Error('Missing impersonation token')
		this.#impersonationToken = token
	}

	clearImpersonationSession() {
		this.#impersonationToken = null
	}

	async impersonationRequest(method, url, params) {
		if (!this.#impersonationToken) throw Object.assign(new Error('Impersonation session required'), { code: 'ADMIN_AUTH_REQUIRED' })
		try {
			return (await this.http.request({
				method, url, params,
				headers: { Authorization: `Impersonation ${this.#impersonationToken}` }, // dedicated read-only control-plane client
			})).data
		} catch (error) {
			if (error.response?.status === 401) this.#impersonationToken = null
			throw error
		}
	}

	impersonationGet(url, params) { return this.impersonationRequest('get', url, params) }
	async endImpersonation() {
		try {
			if (this.#impersonationToken) await this.impersonationRequest('delete', '/api/impersonation/session')
		} finally {
			this.clearImpersonationSession()
		}
	}

	clear() {
		this.#adminToken = null
		this.#impersonationToken = null
	}
}

const adminClient = new AdminClient()
export default adminClient
